import { useNavigate } from "react-router";
import "./OnboardingChecklist.css";

interface Props {
  /** Number of Job Types already configured for this tenant. When > 0,
   *  the card hides itself — it has served its purpose. */
  jobTypeCount: number;
}

/**
 * Fresh-tenant "start here" checklist. Rendered on the Dashboard when a
 * tenant has zero JobTypes configured (nothing else to show them yet).
 *
 * Hides automatically once `jobTypeCount > 0`. The "Dismiss forever"
 * button (future work, RESUME_HERE audit #42) gives tenants an early opt-out
 * before they reach step 1.
 */
export function OnboardingChecklist({ jobTypeCount }: Props) {
  const navigate = useNavigate();

  if (jobTypeCount > 0) {
    return null;
  }

  const steps: Array<{
    n: number;
    title: string;
    hint: string;
    cta: string;
    onClick: () => void;
  }> = [
    {
      n: 1,
      title: "Create a Job Type",
      hint: "A Job Type defines what kind of document you capture (invoices, receipts, permits…) and the pipeline it runs through.",
      cta: "Create Job Type →",
      onClick: () => navigate("/admin/job-types"),
    },
    {
      n: 2,
      title: "Configure form fields",
      hint: "Once you have a Job Type, open its Form Config to define the fields operators will key (name, data type, validation).",
      cta: "Open Job Types →",
      onClick: () => navigate("/admin/job-types"),
    },
    {
      n: 3,
      title: "Connect an input channel",
      hint: "Add a hot folder, email webhook, or scanner so documents can enter the system automatically.",
      cta: "Set up input →",
      onClick: () => navigate("/admin/input-channels"),
    },
    {
      n: 4,
      title: "Configure an export format",
      hint: "Decide how keyed data leaves Capture — CSV, XML, fixed-width, or JSON — and where it goes.",
      cta: "Configure export →",
      onClick: () => navigate("/admin/export-formats"),
    },
    {
      n: 5,
      title: "Upload your first batch",
      hint: "Drop a few sample documents to see the full pipeline in action.",
      cta: "Go to Batches →",
      onClick: () => navigate("/batches"),
    },
  ];

  return (
    <div className="onboarding-checklist form-comfortable" data-testid="onboarding-checklist">
      <div className="onboarding-checklist__header">
        <span className="onboarding-checklist__eyebrow">Get started</span>
        <h2 className="onboarding-checklist__title">Set up your Capture workspace</h2>
        <p className="onboarding-checklist__hint">
          Walk through these five steps to turn a fresh tenant into a working
          document pipeline. You can come back to any step at any time — this
          card disappears once you create your first Job Type.
        </p>
      </div>

      <ol className="onboarding-checklist__steps">
        {steps.map((s) => (
          <li key={s.n} className="onboarding-checklist__step">
            <div className="onboarding-checklist__bullet" aria-hidden="true">
              {s.n}
            </div>
            <div className="onboarding-checklist__step-body">
              <div className="onboarding-checklist__step-title">{s.title}</div>
              <div className="onboarding-checklist__step-hint">{s.hint}</div>
            </div>
            <button
              type="button"
              className="onboarding-checklist__cta"
              onClick={s.onClick}
            >
              {s.cta}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
